/**
 * FollowButton & FavoriteButton — reusable across MentorProfile,
 * MentorCard, and anywhere else a mentor appears.
 *
 * Props:
 *   mentorId  {number}  — mentor's numeric id
 *   size      'sm'|'md' — controls padding/font
 *   showCount {bool}    — show follower count beside Follow btn
 */
import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useFollow } from '../context/FollowContext';
import './FollowButton.css';

export function FollowButton({ mentorId, size = 'md', showCount = false, onLoginRequired }) {
  const { user } = useAuth();
  const { isFollowing, toggleFollow, getFollowerCount } = useFollow();
  const following = isFollowing(mentorId);
  const count     = getFollowerCount(mentorId);

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) { onLoginRequired?.(); return; }
    toggleFollow(mentorId);
  };

  return (
    <button
      className={`follow-btn ${following ? 'following' : ''} size-${size}`}
      onClick={handleClick}
      title={following ? 'Unfollow mentor' : 'Follow mentor to get updates'}
    >
      <span className="follow-ico">{following ? '✓' : '+'}</span>
      <span>{following ? 'Following' : 'Follow'}</span>
      {showCount && <span className="follow-count">{formatCount(count)}</span>}
    </button>
  );
}

export function FavoriteButton({ mentorId, size = 'md', onLoginRequired }) {
  const { user } = useAuth();
  const { isFavorite, toggleFavorite } = useFollow();
  const fav = isFavorite(mentorId);

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) { onLoginRequired?.(); return; }
    toggleFavorite(mentorId);
  };

  return (
    <button
      className={`fav-btn ${fav ? 'active' : ''} size-${size}`}
      onClick={handleClick}
      title={fav ? 'Remove from favourites' : 'Add to favourites'}
    >
      {fav ? '❤️' : '🤍'}
    </button>
  );
}

function formatCount(n) {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}
